import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, UsersRound, BarChart3 } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

function CohortRow({ cohort, headers, onChanged }) {
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const [name, setName] = useState(cohort.name);

  const save = async () => {
    try {
      await axios.put(`${API}/admin/cohorts/${cohort.cohort_id}`, { name }, { headers });
      toast.success('Cohort renamed');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rename cohort'); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${cohort.name}"? Its ${cohort.member_count} learner(s) will be left without a cohort and it will be unlinked from ${cohort.programme_count} programme(s).`)) return;
    try {
      await axios.delete(`${API}/admin/cohorts/${cohort.cohort_id}`, { headers });
      toast.success('Cohort deleted');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete cohort'); }
  };

  return (
    <tr className="hover:bg-zinc-800/30" data-testid={`cohort-row-${cohort.cohort_id}`}>
      <td className="px-6 py-4">
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && save()}
            className="h-9 w-full max-w-sm bg-navigate-dark border border-zinc-700 rounded-md text-sm text-white px-3" data-testid={`cohort-name-input-${cohort.cohort_id}`} />
        ) : <span className="text-sm font-medium text-white" data-testid={`cohort-name-${cohort.cohort_id}`}>{cohort.name}</span>}
      </td>
      <td className="px-6 py-4 text-sm text-zinc-300">{cohort.member_count}</td>
      <td className="px-6 py-4 text-sm text-zinc-300">{cohort.programme_count}</td>
      <td className="px-6 py-4">
        <div className="flex gap-1">
          {editing ? (<>
            <button onClick={save} className="p-2 rounded-lg text-green-400 hover:bg-green-500/10" data-testid={`cohort-save-${cohort.cohort_id}`}><Check className="h-4 w-4" /></button>
            <button onClick={() => { setEditing(false); setName(cohort.name); }} className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-800"><X className="h-4 w-4" /></button>
          </>) : (<>
            <button onClick={() => navigate(`/admin/cohorts/${cohort.cohort_id}/analytics`)} className="p-2 rounded-lg text-teal-300 hover:bg-teal-500/10" title="Analytics" data-testid={`cohort-analytics-${cohort.cohort_id}`}><BarChart3 className="h-4 w-4" /></button>
            <button onClick={() => setEditing(true)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid={`cohort-edit-${cohort.cohort_id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={remove} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10" data-testid={`cohort-delete-${cohort.cohort_id}`}><Trash2 className="h-4 w-4" /></button>
          </>)}
        </div>
      </td>
    </tr>
  );
}

export default function CohortsAdmin({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [cohorts, setCohorts] = useState(null);
  const [name, setName] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/admin/cohorts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCohorts(r.data.cohorts || []))
      .catch(() => { setCohorts([]); toast.error('Failed to load cohorts'); });
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/cohorts`, { name }, { headers });
      toast.success('Cohort created');
      setName('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create cohort'); }
  };

  return (
    <div className="p-8" data-testid="cohorts-admin">
      <h1 className="font-heading text-3xl font-bold text-white mb-1">Cohorts</h1>
      <p className="text-sm text-zinc-400 mb-8">Group learners into cohorts. Assign a learner's cohort in Users, and link cohorts to programmes in the Programme Builder.</p>
      <form onSubmit={create} className="flex gap-3 mb-8 max-w-xl">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New cohort name, e.g. 2026 Intake A"
          className="flex-1 h-10 bg-navigate-surface border border-zinc-800 rounded-lg text-sm text-white px-3" data-testid="new-cohort-name-input" />
        <button type="submit" disabled={!name.trim()} className="btn-gold px-4 text-sm flex items-center gap-1 disabled:opacity-50" data-testid="create-cohort-btn"><Plus className="h-4 w-4" />Create cohort</button>
      </form>
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50"><tr>
            {['Cohort', 'Learners', 'Programmes', 'Actions'].map((h) => <th key={h} className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {cohorts === null ? <tr><td colSpan={4} className="px-6 py-8 text-center text-zinc-500">Loading...</td></tr>
              : cohorts.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-zinc-500" data-testid="cohorts-empty">
                  <UsersRound className="h-8 w-8 mx-auto mb-2 text-zinc-600" />No cohorts yet. Create your first one above.
                </td></tr>
              ) : cohorts.map((c) => <CohortRow key={c.cohort_id} cohort={c} headers={headers} onChanged={load} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
